import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Browser-side counterpart to api/src/lib/logger.ts. This is the single seam
// for frontend logging — the only place to change to swap the mechanism or
// add/remove a destination.
//
// Where a log line ends up:
//
//   log.info('msg', { ctx }) ─→ console.info        (devtools, local only)
//                            └─→ Sentry.logger.info (Logs tab, searchable)
//
//   log.error / log.fatal carrying an Error additionally becomes a Sentry
//   Issue via captureException — the alertable, grouped, stack-traced view.
//
// There is no CloudWatch path here: browser logs can only reach Sentry.
//
// Two things worth knowing:
//
//   * Sentry delivery requires `enableLogs: true` in the app's Sentry.init.
//     Without it Sentry.logger.* silently no-ops, and so does this module.
//   * We forward structured attributes explicitly rather than relying on
//     consoleLoggingIntegration. That integration turns `('msg', { ctx })`
//     into a formatted string with the context flattened into
//     `sentry.message.parameter.N`, which is not queryable. Calling
//     Sentry.logger directly keeps { ctx } as real attributes. It is also why
//     consoleLoggingIntegration is no longer registered in either app — with
//     both active every line would be reported twice.
// ---------------------------------------------------------------------------

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

type Attributes = Record<string, unknown>;

// console has no trace/fatal equivalent worth using — console.trace prints a
// stack rather than the message.
const CONSOLE_METHOD: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  debug: 'debug',
  error: 'error',
  fatal: 'error',
  info: 'info',
  trace: 'debug',
  warn: 'warn',
};

const isMergingObject = (value: unknown): value is Attributes =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Error);

// Errors don't survive as Sentry log attributes (their own properties aren't
// enumerable), so we flatten the useful parts and hand the instance itself to
// captureException instead.
const describeError = (error: Error): Attributes => ({
  errorMessage: error.message,
  errorName: error.name,
});

const findError = (values: unknown[]): Error | undefined => {
  for (const value of values) {
    if (value instanceof Error) {
      return value;
    }
    if (isMergingObject(value)) {
      const nested = value.err ?? value.error;
      if (nested instanceof Error) {
        return nested;
      }
    }
  }
  return undefined;
};

const toAttributes = (rest: unknown[]): Attributes => {
  if (rest.length === 0) {
    return {};
  }
  if (rest.length === 1) {
    const [only] = rest;
    if (only instanceof Error) {
      return describeError(only);
    }
    if (isMergingObject(only)) {
      const { err, error, ...others } = only;
      const nested = err ?? error;
      return nested instanceof Error ? { ...others, ...describeError(nested) } : only;
    }
    return { detail: only };
  }
  return { details: rest };
};

// Accepts both call styles, matching the API seam:
//
//   log.error('Fetch failed', error)            — console-style
//   log.error({ msg: 'Fetch failed', error })   — pino-style
const emit = (level: LogLevel, first: unknown, rest: unknown[]): void => {
  let text: string;
  let attributes: Attributes;

  if (typeof first === 'string') {
    text = first;
    attributes = toAttributes(rest);
  } else if (isMergingObject(first)) {
    const { message, msg, ...others } = first;
    text = typeof msg === 'string' ? msg : typeof message === 'string' ? message : '';
    attributes = toAttributes([others]);
  } else if (first instanceof Error) {
    text = first.message;
    attributes = describeError(first);
  } else {
    text = String(first);
    attributes = {};
  }

  // Devtools first, so a Sentry problem can't cost us local debuggability.
  // eslint-disable-next-line no-console
  console[CONSOLE_METHOD[level]](first, ...rest);

  Sentry.logger[level](text, attributes);

  if (level === 'error' || level === 'fatal') {
    const error = findError([first, ...rest]);
    if (error) {
      Sentry.captureException(error, { extra: attributes });
    }
  }
};

export const log = {
  debug: (first: unknown, ...rest: unknown[]): void => emit('debug', first, rest),
  error: (first: unknown, ...rest: unknown[]): void => emit('error', first, rest),
  fatal: (first: unknown, ...rest: unknown[]): void => emit('fatal', first, rest),
  info: (first: unknown, ...rest: unknown[]): void => emit('info', first, rest),
  trace: (first: unknown, ...rest: unknown[]): void => emit('trace', first, rest),
  warn: (first: unknown, ...rest: unknown[]): void => emit('warn', first, rest),
};
